import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Lock, User, Loader2, LogIn, UserPlus, History, Award, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper: set the default Authorization header for all axios requests
function setAxiosToken(token) {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
}

export function AuthModal({ isOpen, onClose, onAuthSuccess }) {
  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const endpoint = mode === 'login' ? '/auth/login' : '/auth/register';
      const payload = mode === 'login' 
        ? { email, password }
        : { email, password, name };

      const response = await axios.post(`${API}${endpoint}`, payload);

      // Store token from response
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
        setAxiosToken(response.data.session_token);
      }

      toast.success(mode === 'login' ? 'Bine ai revenit!' : 'Cont creat cu succes!');
      onAuthSuccess(response.data);
      onClose();
    } catch (error) {
      const message = error.response?.data?.detail || 'Eroare la autentificare';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-card border-2 border-primary/20 rounded-lg shadow-2xl w-full max-w-md overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-primary text-white p-6 relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white/70 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-2xl font-serif font-bold">
              {mode === 'login' ? 'Autentificare' : 'Înregistrare'}
            </h2>
            <p className="text-white/70 text-sm mt-1">
              {mode === 'login' 
                ? 'Accesează contul tău pentru istoric și discounturi' 
                : 'Creează un cont pentru a salva dosarele'}
            </p>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Email Form */}
            <form onSubmit={handleEmailAuth} className="space-y-4">
              {mode === 'register' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Nume complet</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border-2 border-border rounded-lg focus:border-primary outline-none transition-colors"
                      placeholder="Ion Popescu"
                      required
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border-2 border-border rounded-lg focus:border-primary outline-none transition-colors"
                    placeholder="email@exemplu.ro"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">Parolă</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border-2 border-border rounded-lg focus:border-primary outline-none transition-colors"
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 bg-primary text-white"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : mode === 'login' ? (
                  <><LogIn className="w-5 h-5 mr-2" />Intră în cont</>
                ) : (
                  <><UserPlus className="w-5 h-5 mr-2" />Creează cont</>
                )}
              </Button>
            </form>

            {/* Switch mode */}
            <p className="text-center text-sm text-muted-foreground mt-6">
              {mode === 'login' ? (
                <>Nu ai cont? <button onClick={() => setMode('register')} className="text-primary font-semibold hover:underline">Înregistrează-te</button></>
              ) : (
                <>Ai deja cont? <button onClick={() => setMode('login')} className="text-primary font-semibold hover:underline">Autentifică-te</button></>
              )}
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export function UserMenu({ user, onLogout }) {
  const [isOpen, setIsOpen] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user) {
      fetchStats();
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/user/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
      localStorage.removeItem('session_token');
      setAxiosToken(null);
      toast.success('Deconectat cu succes');
      onLogout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted/50 transition-colors"
      >
        {user.picture ? (
          <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-semibold">
            {user.name?.charAt(0).toUpperCase()}
          </div>
        )}
        <span className="text-sm font-medium hidden sm:block">{user.name}</span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute right-0 top-full mt-2 w-64 bg-card border-2 border-primary/10 rounded-lg shadow-xl z-50"
          >
            <div className="p-4 border-b border-border">
              <p className="font-semibold text-primary">{user.name}</p>
              <p className="text-sm text-muted-foreground">{user.email}</p>
            </div>

            {stats && (
              <div className="p-4 border-b border-border space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <History className="w-4 h-4 text-secondary" />
                  <span>Dosare analizate: <strong>{stats.cases_analyzed}</strong></span>
                </div>
                {stats.discount_percent > 0 && (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <Award className="w-4 h-4" />
                    <span>Discount: <strong>{stats.discount_percent}%</strong></span>
                  </div>
                )}
                <div className="text-xs text-muted-foreground mt-1">
                  Tip abonament: <span className="capitalize">{stats.subscription_type}</span>
                </div>
              </div>
            )}

            <div className="p-2">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Deconectare
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function useAuth() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Load token from localStorage on mount
    const token = localStorage.getItem('session_token');
    if (token) {
      setAxiosToken(token);
    }
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      setUser(null);
      setIsLoading(false);
      return;
    }
    
    setAxiosToken(token);
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      // Token expired or invalid
      localStorage.removeItem('session_token');
      setAxiosToken(null);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = (userData) => {
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('session_token');
    setAxiosToken(null);
    setUser(null);
  };

  return { user, isLoading, login, logout };
}
